import {
  Button,
  Container,
  Grid,
  Input,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import PropTypes from "prop-types";

export default function BulkUploadData({ collectionName, uploadService }) {
  const [excelData, setExcelData] = useState([]);
  const fileInputRef = useRef();

  const handleBulkUploadFile = (event) => {
    const file = event.target.files[0];
    if (!file) {
      alert("Please select a valid Excel file.");
      resetFileInput();
      return;
    }

    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension !== "xlsx" && fileExtension !== "xls") {
      alert("Invalid file type. Please upload an Excel file (.xlsx or .xls).");
      resetFileInput();
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const bstr = event.target.result;
        const workBook = XLSX.read(bstr, { type: "binary" });
        const workSheetName = workBook.SheetNames[0];
        console.log("workSheetName : ", workSheetName);

        const workSheetData = workBook.Sheets[workSheetName];
        const jsonData = XLSX.utils.sheet_to_json(workSheetData);

        if (!jsonData || jsonData.length === 0) {
          console.log("Excel file is empty or has incorrect format.");
        } else {
          if (workSheetName === "blogs") {
            setExcelData(
              jsonData.map((row) => ({
                title: row.title,
                category: row.category,
                date: row.date,
                image: row.image,
                points: row.points
                  ? row.points.split("|").map((point) => point.trim())
                  : [], // 🔥 Split points into array
              }))
            );
          } else if (workSheetName === "recipes") {
            console.log("We are in recipe sheet");
            setExcelData(
              jsonData.map((row) => ({
                image: row.image,
                title: row.title,
                category: row.category,
                description: row.description,
                videoURL: row.videoURL,
                ingredients: row.ingredients
                  ? row.ingredients
                      .split("|")
                      .map((ingredient) => ingredient.trim())
                  : [], // 🔥 Split ingredients into array
                steps: row.steps
                  ? row.steps.split("|").map((step) => step.trim())
                  : [], // 🔥 Split steps into array
              }))
            );
          } else {
            setExcelData(jsonData);
          }

          console.log(
            `Bulk ${collectionName} data to be uploaded : `,
            jsonData
          );
        }
      } catch (err) {
        console.log(
          "Error parsing file: Failed to parse the Excel file. Please check the file content.",
          err
        );
        resetFileInput();
      }
    };
    reader.readAsBinaryString(file);
  };

  const uploadBulkData = async () => {
    if (excelData && excelData.length > 0) {
      for (const dataObj of excelData) {
        uploadService(dataObj);
      }
      alert(`Bulk ${collectionName} upload is successfull.`);
      setExcelData([]); // Clear preview table
    } else {
      alert("No data to upload.");
    }
    resetFileInput();
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Container>
      <Grid sx={{ p: 3 }}>
        <Typography variant="h5">Bulk Upload {collectionName}</Typography>
        <Input
          type="file"
          inputProps={{ accept: ".xlsx, .xls" }}
          onChange={handleBulkUploadFile}
          inputRef={fileInputRef}
        />

        {/* ✅ Preview Table */}
        {excelData.length > 0 && (
          <TableContainer component={Paper} sx={{ mt: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {/* Dynamically show table headers based on keys */}
                  {Object.keys(excelData[0]).map((key, index) => (
                    <TableCell key={index} sx={{ fontWeight: "bold" }}>
                      {key.toUpperCase()}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {excelData.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {Object.values(row).map((value, colIndex) => (
                      <TableCell key={colIndex}>{value}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Button
          onClick={uploadBulkData}
          variant="contained"
          color="primary"
          sx={{ m: 3 }}
        >
          Upload Data to Database
        </Button>
      </Grid>
    </Container>
  );
}

BulkUploadData.propTypes = {
  collectionName: PropTypes.string.isRequired,
  uploadService: PropTypes.func.isRequired,
};
